import * as React from "react";
import {
  ControlButton,
  Controls,
  useReactFlow,
  useStore,
  type Edge,
  type Node,
} from "@xyflow/react";

import {
  computeWorkspaceCanvasViewportForNodes,
  getWorkspaceGraphCenter,
  WORKSPACE_CANVAS_MAX_ZOOM,
  WORKSPACE_CANVAS_MIN_ZOOM,
  zoomWorkspaceViewportAtGraph,
} from "./workspaceCanvasViewport";
import type { WorkspaceFlowNodeData } from "./workspaceGraphUtils";

type WorkspaceFlowNode = Node<WorkspaceFlowNodeData>;

function ReactFlowPlusIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden>
      <path d="M32 18.133H18.133V32h-4.266V18.133H0v-4.266h13.867V0h4.266v13.867H32z" />
    </svg>
  );
}

function ReactFlowMinusIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 5" aria-hidden>
      <path d="M0 0h32v4.2H0z" />
    </svg>
  );
}

function ReactFlowFitViewIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 30" aria-hidden>
      <path d="M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.139h-3.692v5.139zm-23.677.94c-.531 0-.939-.4-.939-.94v-5.138H0v5.139c0 2.577 2.13 4.707 4.708 4.707h5.138V25.77H4.631z" />
    </svg>
  );
}

export type WorkspaceCanvasControlsProps = {
  /** Extra CSS bottom offset so controls clear the pine dock overlay. */
  bottomOffsetPx?: number;
  /** Fit-view bottom padding matching the dock's reserved band. */
  fitBottomPadding?: number;
};

export function WorkspaceCanvasControls({
  bottomOffsetPx = 12,
  fitBottomPadding,
}: WorkspaceCanvasControlsProps = {}): React.ReactElement {
  const { getViewport, setViewport, getNodes } = useReactFlow<WorkspaceFlowNode, Edge>();
  const width = useStore((state) => state.width);
  const height = useStore((state) => state.height);
  const minZoomReached = useStore((state) => state.transform[2] <= state.minZoom);
  const maxZoomReached = useStore((state) => state.transform[2] >= state.maxZoom);

  const handleZoomIn = React.useCallback(() => {
    const nodes = getNodes();
    if (nodes.length === 0) return;

    const graphCenter = getWorkspaceGraphCenter(nodes);
    const nextViewport = zoomWorkspaceViewportAtGraph(getViewport(), graphCenter, "in", {
      minZoom: WORKSPACE_CANVAS_MIN_ZOOM,
      maxZoom: WORKSPACE_CANVAS_MAX_ZOOM,
    });
    void setViewport(nextViewport, { duration: 150 });
  }, [getNodes, getViewport, setViewport]);

  const handleZoomOut = React.useCallback(() => {
    const nodes = getNodes();
    if (nodes.length === 0) return;

    const graphCenter = getWorkspaceGraphCenter(nodes);
    const nextViewport = zoomWorkspaceViewportAtGraph(getViewport(), graphCenter, "out", {
      minZoom: WORKSPACE_CANVAS_MIN_ZOOM,
      maxZoom: WORKSPACE_CANVAS_MAX_ZOOM,
    });
    void setViewport(nextViewport, { duration: 150 });
  }, [getNodes, getViewport, setViewport]);

  const handleFitView = React.useCallback(() => {
    const nodes = getNodes();
    if (nodes.length === 0 || width <= 0 || height <= 0) return;

    const viewport = computeWorkspaceCanvasViewportForNodes(nodes, width, height, {
      bottomPadding: fitBottomPadding,
    });
    void setViewport(viewport, { duration: 200 });
  }, [fitBottomPadding, getNodes, height, setViewport, width]);

  return (
    <Controls
      showInteractive={false}
      showZoom={false}
      showFitView={false}
      position="bottom-right"
      className="!right-3"
      style={{
        bottom: bottomOffsetPx,
        transition: "bottom 360ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <ControlButton
        className="react-flow__controls-zoomin"
        onClick={handleZoomIn}
        title="Zoom in"
        aria-label="Zoom in"
        disabled={maxZoomReached}
      >
        <ReactFlowPlusIcon />
      </ControlButton>
      <ControlButton
        className="react-flow__controls-zoomout"
        onClick={handleZoomOut}
        title="Zoom out"
        aria-label="Zoom out"
        disabled={minZoomReached}
      >
        <ReactFlowMinusIcon />
      </ControlButton>
      <ControlButton
        className="react-flow__controls-fitview"
        onClick={handleFitView}
        title="Fit view"
        aria-label="Fit view"
      >
        <ReactFlowFitViewIcon />
      </ControlButton>
    </Controls>
  );
}
